import React, {useCallback, useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import createAxiosInstance, {toQueryParams} from '../support/AxiosSupport';
import {fetchRef} from '../service/refsCache';
import {useWardrobe} from '../context/WardrobeContext';
import AuthImage from './AuthImage';
import OutfitScoreBadge, {OutfitScoreDiagnostic} from './OutfitScoreBadge';
import OutfitScoreDetails from './OutfitScoreDetails';
import WeatherLocationForm, {FIXABLE_WEATHER_REASONS} from './WeatherLocationForm';
import {slotLabel} from '../support/outfitLabels';
import {AlertTriangleIcon, CloudIcon, CloudRainIcon, MapPinIcon, RefreshIcon, SnowflakeIcon, SunIcon} from './icons';

interface OutfitItem {
    id: string;
    name: string;
    brandName: string | null;
    layerName: string | null;
    rating: number | null;
    primaryPhotoUrl: string | null;
    /** Слот в образе (BASE/…/ACCESSORY) — подпись роли вещи; layerName — лишь фолбэк. */
    slot?: string | null;
}

interface OutfitVariant {
    variant: number;
    items: OutfitItem[];
    reasons: string[];
    /** OutfitScore 0–100 движка (outfit-score-engine); null — образ не оценён. */
    score: number | null;
    outfitScore?: number | null;
    scoreLabel?: string | null;
    topDiagnostics?: OutfitScoreDiagnostic[];
    /** Полнота образа (CTHS-87): неполный набор показывается заготовкой с перечнем слотов. */
    complete?: boolean;
    missingSlots?: string[];
}

interface WeatherForecast {
    available: boolean;
    reason: string | null;
    cached: boolean;
    date: string | null;
    city: string | null;
    tempMinC: number | null;
    tempMaxC: number | null;
    precipitationMm: number | null;
    windSpeedKmh: number | null;
    snow: boolean | null;
    cloudCoverPct: number | null;
    warmthCode: string | null;
    seasonCode: string | null;
    weatherSuitabilityCodes: string[] | null;
}

interface DailyOutfits {
    weather: WeatherForecast | null;
    weatherApplied: boolean;
    occasion: string | null;
    variants: OutfitVariant[];
}

interface RefOption {
    code: string;
    nameRu: string;
}

const fmtTemp = (v: number | null | undefined): string => {
    if (v == null) return '—';
    const r = Math.round(v);
    return r > 0 ? `+${r}` : `${r}`;
};

const tempRange = (w: WeatherForecast): string => {
    if (w.tempMinC == null && w.tempMaxC == null) return '';
    if (w.tempMinC != null && w.tempMaxC != null && Math.round(w.tempMinC) !== Math.round(w.tempMaxC)) {
        return `${fmtTemp(w.tempMinC)}…${fmtTemp(w.tempMaxC)} °C`;
    }
    return `${fmtTemp(w.tempMaxC ?? w.tempMinC)} °C`;
};

/**
 * Главная карточка «Образ дня» (CTHS-29): погода дня + контекстный образ с причиной выбора и
 * действиями «Надеть»/«Другой вариант». Сам грузит данные с /outfits/daily (CTHS-28); погода
 * прозрачно деградирует — при отсутствии локации/согласия показывается понятный CTA включить
 * погоду (PUT /weather/location, CTHS-27), образ при этом всё равно подбирается по рейтингу/контексту.
 *
 * <p>Owner-scoped через WardrobeContext (без IDOR на бэкенде). Только семантические токены и
 * dark:-варианты, тач-зоны ≥44px — по styleguide.</p>
 */
const HomeOutfitCard: React.FC = () => {
    const navigate = useNavigate();
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();

    const [data, setData] = useState<DailyOutfits | null>(null);
    const [variantIndex, setVariantIndex] = useState(0);
    const [exclude, setExclude] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [acceptMsg, setAcceptMsg] = useState<string | null>(null);
    const [accepting, setAccepting] = useState(false);

    const [warmthLabels, setWarmthLabels] = useState<Record<string, string>>({});
    const [weatherLabels, setWeatherLabels] = useState<Record<string, string>>({});

    const canEdit = isOwnWardrobe || (permissions?.includes('EDIT_ITEMS') ?? false);

    const load = useCallback(async (excludeIds: string[], signal?: AbortSignal) => {
        setLoading(true);
        setError(false);
        setAcceptMsg(null);
        try {
            const api = await createAxiosInstance();
            const params: Record<string, unknown> = {};
            if (selectedOwnerId) params.ownerId = selectedOwnerId;
            if (excludeIds.length > 0) params.exclude = excludeIds;
            const res = await api.get<DailyOutfits>('/outfits/daily', {params: toQueryParams(params), signal});
            setData(res.data);
            setVariantIndex(0);
        } catch (e) {
            if (e instanceof Error && e.name === 'CanceledError') return;
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [selectedOwnerId]);

    // Справочные RU-метки кодов погоды (необязательны — при сбое показываем сырой код).
    useEffect(() => {
        (async () => {
            try {
                const [warmth, weather] = await Promise.all([
                    fetchRef<RefOption[]>('warmth-levels'),
                    fetchRef<RefOption[]>('weather-suitabilities'),
                ]);
                setWarmthLabels(Object.fromEntries(warmth.map(o => [o.code, o.nameRu])));
                setWeatherLabels(Object.fromEntries(weather.map(o => [o.code, o.nameRu])));
            } catch (e) {
                // метки остаются пустыми — рендерим коды как есть
            }
        })();
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setExclude([]);
        load([], controller.signal);
        return () => controller.abort();
    }, [load]);

    const variants = data?.variants ?? [];
    const current = variants[variantIndex] ?? null;

    // «Другой вариант»: сначала листаем уже полученные варианты (мгновенно, без запроса),
    // когда они кончились — добираем свежий набор с исключением всех показанных вещей (CTHS-4).
    // Кнопка заблокирована во время загрузки — защита от частых повторных нажатий (edge case).
    const another = () => {
        if (!data || loading) return;
        if (variantIndex + 1 < variants.length) {
            setVariantIndex(variantIndex + 1);
            setAcceptMsg(null);
            return;
        }
        const shown = variants.flatMap(v => v.items.map(i => i.id));
        const next = Array.from(new Set([...exclude, ...shown]));
        setExclude(next);
        load(next);
    };

    const reset = () => {
        setExclude([]);
        load([]);
    };

    // «Надеть»: сначала create-or-reuse сохранённого образа (дедуп по составу на бэкенде —
    // повторный тот же набор переиспользует существующий образ, а не плодит копию), затем
    // отмечаем носку и переходим на страницу образа, где доступна точечная замена вещей.
    const accept = async () => {
        if (!current || accepting) return;
        setAccepting(true);
        setAcceptMsg(null);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const created = await api.post<{id: string}>('/outfits/saved',
                {name: 'Образ дня', itemIds: current.items.map(i => i.id)}, {params});
            const outfitId = created.data.id;
            const w = data?.weather;
            try {
                await api.post(`/outfits/saved/${outfitId}/wear`, {
                    occasion: data?.occasion ?? undefined,
                    weatherTempMinC: w?.tempMinC != null ? Math.round(w.tempMinC) : undefined,
                    weatherTempMaxC: w?.tempMaxC != null ? Math.round(w.tempMaxC) : undefined,
                }, {params});
            } catch { /* носка не критична для перехода */ }
            navigate(`/outfits/saved/${outfitId}`);
        } catch (e) {
            setAcceptMsg('Не удалось отметить образ');
            setAccepting(false);
        }
    };

    // Форма включения погоды вынесена в WeatherLocationForm (общая с подбором образа дня).
    const onLocationSaved = async () => {
        setExclude([]);
        await load([]);
    };

    if (loading) {
        return (
            <div className="card p-6 animate-pulse" data-testid="home-outfit-loading">
                <div className="h-5 w-40 bg-gray-200 dark:bg-ink-700 rounded mb-4"/>
                <div className="h-20 bg-gray-100 dark:bg-ink-800 rounded-xl mb-3"/>
                <div className="h-24 bg-gray-100 dark:bg-ink-800 rounded-xl"/>
            </div>
        );
    }

    if (error) {
        return (
            <div className="card p-6 space-y-4" data-testid="home-outfit-error">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Образ дня</h2>
                <p className="text-gray-600 dark:text-gray-300">Не удалось загрузить образ дня.</p>
                <button
                    type="button"
                    onClick={() => load(exclude)}
                    data-testid="home-outfit-retry"
                    className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                >
                    Повторить
                </button>
            </div>
        );
    }

    const weather = data?.weather ?? null;
    const weatherFixable = !!weather && !weather.available && !!weather.reason
        && FIXABLE_WEATHER_REASONS.has(weather.reason);
    const weatherProviderDown = !!weather && !weather.available && weather.reason === 'PROVIDER_UNAVAILABLE';

    const WeatherIcon = weather?.snow
        ? SnowflakeIcon
        : (weather && (weather.precipitationMm ?? 0) > 0
            ? CloudRainIcon
            : ((weather?.cloudCoverPct ?? 0) >= 50 ? CloudIcon : SunIcon));

    const conditionLabels = (weather?.weatherSuitabilityCodes ?? [])
        .map(c => weatherLabels[c] ?? c);

    return (
        <div className="card p-6 space-y-4" data-testid="home-outfit">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Образ дня</h2>
            </div>

            {/* Погодный блок / CTA включить погоду */}
            {data?.weatherApplied && weather?.available ? (
                <div
                    className="flex items-center gap-3 rounded-xl bg-gray-50 dark:bg-ink-800 p-4"
                    data-testid="home-outfit-weather"
                >
                    <WeatherIcon className="w-8 h-8 text-brand-600 dark:text-brand-400 shrink-0"/>
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                            <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                                {tempRange(weather)}
                            </span>
                            {weather.city && (
                                <span className="text-sm text-gray-500 dark:text-gray-400 inline-flex items-center gap-1">
                                    <MapPinIcon className="w-4 h-4"/>{weather.city}
                                </span>
                            )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                            {weather.warmthCode && (warmthLabels[weather.warmthCode] ?? weather.warmthCode)}
                            {conditionLabels.length > 0 && (
                                <span> · {conditionLabels.join(', ')}</span>
                            )}
                        </div>
                    </div>
                </div>
            ) : weatherFixable ? (
                <WeatherLocationForm testIdPrefix="home-outfit" onSaved={onLocationSaved}/>
            ) : weatherProviderDown ? (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="home-outfit-weather-down">
                    Погода временно недоступна — образ подобран по рейтингу и контексту.
                </p>
            ) : null}

            {/* Образ */}
            {current ? (
                <div className="space-y-3" data-testid="home-outfit-variant">
                    {/* outfit-score-engine: единая оценка сочетаемости варианта с объяснением */}
                    <div className="flex flex-wrap items-center gap-2">
                        <OutfitScoreBadge score={current.outfitScore ?? current.score}
                                          label={current.scoreLabel}/>
                    </div>
                    {/* F09: краткие причины сразу под баллом, техника расчёта — по раскрытию. */}
                    {(current.topDiagnostics ?? []).length > 0 && (
                        <OutfitScoreDetails diagnostics={current.topDiagnostics as OutfitScoreDiagnostic[]}/>
                    )}
                    {current.complete === false && (
                        <p className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-400"
                           data-testid="home-outfit-incomplete">
                            <AlertTriangleIcon className="w-4 h-4 shrink-0 mt-0.5"/>
                            <span>
                                Заготовка образа — не хватает: {(current.missingSlots ?? []).join(', ') || 'обязательных слотов'}
                            </span>
                        </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {current.items.map(item => (
                            <Link
                                key={item.id}
                                to={`/clothing/${item.id}`}
                                data-testid="home-outfit-item-link"
                                title={`Открыть карточку: ${item.name}`}
                                className="rounded-xl border border-gray-200 dark:border-ink-700 overflow-hidden block hover:border-brand-400 hover:shadow-md transition-[border-color,box-shadow] duration-ui ease-out focus:outline-none focus:ring-2 focus:ring-brand-400"
                            >
                                <div className="aspect-square bg-gray-100 dark:bg-ink-800">
                                    {item.primaryPhotoUrl && (
                                        <AuthImage imageKey={item.primaryPhotoUrl} alt={item.name} variant="thumbnail"
                                                   className="w-full h-full object-cover"/>
                                    )}
                                </div>
                                <div className="p-2 min-h-[44px]">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                        {item.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {slotLabel(item.slot, item.layerName) || 'Без слоя'}
                                        {item.brandName && <span> · {item.brandName}</span>}
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {current.reasons.length > 0 && (
                        <div className="rounded-xl bg-gray-50 dark:bg-ink-800 p-3" data-testid="home-outfit-reasons">
                            <p className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-1">
                                Почему этот образ
                            </p>
                            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-0.5 list-disc list-inside">
                                {current.reasons.map((r, i) => (
                                    <li key={i}>{r}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="flex flex-wrap items-center gap-3">
                        {canEdit && (
                            <button
                                type="button"
                                onClick={accept}
                                disabled={accepting}
                                data-testid="home-outfit-accept"
                                className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50"
                            >
                                Надеть
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={another}
                            disabled={loading}
                            data-testid="home-outfit-another"
                            className="min-h-[44px] px-4 rounded-xl border border-gray-300 dark:border-ink-700 text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-ink-700 disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            <RefreshIcon className="w-4 h-4"/>
                            Другой вариант
                        </button>
                        {acceptMsg && (
                            <span className="text-sm text-gray-600 dark:text-gray-300" role="status" data-testid="home-outfit-accept-msg">
                                {acceptMsg}
                            </span>
                        )}
                    </div>
                </div>
            ) : exclude.length > 0 ? (
                // «Другой вариант» исчерпал подходящие вещи — образ пуст не из-за пустого гардероба.
                <div className="space-y-3" data-testid="home-outfit-exhausted">
                    <p className="text-gray-500 dark:text-gray-400">
                        Другие варианты закончились — вы перебрали все подходящие вещи. Можно начать заново.
                    </p>
                    <button
                        type="button"
                        onClick={reset}
                        data-testid="home-outfit-reset"
                        className="min-h-[44px] px-5 rounded-xl bg-brand-600 text-white hover:bg-brand-700"
                    >
                        Начать заново
                    </button>
                </div>
            ) : (
                <p className="text-gray-500 dark:text-gray-400" data-testid="home-outfit-empty">
                    Не удалось собрать образ — добавьте больше вещей в гардероб.
                </p>
            )}
        </div>
    );
};

export default HomeOutfitCard;
